import { inject, Pipe, PipeTransform } from '@angular/core';
import { dateTimeFacade } from '@cf/core';
import { TranslateService } from '@tolgee/ngx';

@Pipe({ name: 'duration', pure: false })
export class DurationPipe implements PipeTransform {
  private translateService = inject(TranslateService);

  // Simple cache
  private cache = new Map<string, string>();

  transform(value: string, numeric = true, textVariant: 'sm' | 'md' | 'lg' = 'sm'): string {
    if (!value) {
      value = '00:00:00';
    }

    // Generate unique key including language
    const key = `${value}_${textVariant}_${numeric}_${this.translateService.language}`;

    // Return from cache if available
    if (this.cache.has(key)) {
      return this.cache.get(key) || '';
    }

    // For numeric format, we can return immediately
    if (numeric) {
      const result = dateTimeFacade.timeToDuration(value).toString();
      this.cache.set(key, result);
      return result;
    }

    // For text format, get instant translation
    const duration = dateTimeFacade.friendlyDurationName(value);
    const result = this.getFormattedDuration(duration, textVariant);

    // Cache the result
    this.cache.set(key, result);

    // If cache gets too large, clear older entries
    if (this.cache.size > 5) {
      this.cleanCache();
    }

    return result;
  }

  private getFormattedDuration(
    duration: { days?: number; hours?: number; minutes?: number },
    textVariant: 'sm' | 'md' | 'lg',
  ): string {
    const result = [];
    if (duration.days) {
      const daysText =
        textVariant === 'sm'
          ? this.translateService.instant('durations_short.days', { value: duration.days })
          : textVariant === 'md'
            ? this.translateService.instant('durations_medium.days', { value: duration.days })
            : this.translateService.instant('durations_full.days_plural', { value: duration.days });
      result.push(daysText);
    }

    if (duration.hours) {
      const hoursText =
        textVariant === 'sm'
          ? this.translateService.instant('durations_short.hours', { value: duration.hours })
          : textVariant === 'md'
            ? this.translateService.instant('durations_medium.hours', { value: duration.hours })
            : this.translateService.instant('durations_full.hours_plural', {
                value: duration.hours,
              });
      result.push(hoursText);
    }

    if (duration.minutes) {
      const minutesText =
        textVariant === 'sm'
          ? this.translateService.instant('durations_short.minutes', { value: duration.minutes })
          : textVariant === 'md'
            ? this.translateService.instant('durations_medium.minutes', { value: duration.minutes })
            : this.translateService.instant('durations_full.minutes_plural', {
                value: duration.minutes,
              });
      result.push(minutesText);
    }

    return result.join(' ');
  }

  private cleanCache(): void {
    // Keep only the most recent 50 entries
    const entriesToKeep = Array.from(this.cache.entries()).slice(-50);

    this.cache.clear();

    entriesToKeep.forEach(([key, value]) => {
      this.cache.set(key, value);
    });
  }
}
